import { RIBBON_PALETTES } from "./palettes";
import { FRAGMENT_SHADER, VERTEX_SHADER, createFragmentShader, } from "./shaders/base-shader";
/**
 * Shader compilation and the per-context program cache, ported from the
 * prototype's MorphingBackground.tsx. Programs are specialised per resolved
 * state (curve / funnel / pointer) and per preset constants, and prewarmed
 * through KHR_parallel_shader_compile when the driver offers it.
 */
export function compileShader(gl, type, source, deferErrors = false) {
    const shader = gl.createShader(type);
    if (!shader)
        throw new Error("Unable to create WebGL shader");
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!deferErrors && !gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const message = gl.getShaderInfoLog(shader) ?? "Unknown shader error";
        gl.deleteShader(shader);
        throw new Error(message);
    }
    return shader;
}
export function createProgram(gl, fragmentSource = FRAGMENT_SHADER, deferCompletion = false) {
    const program = gl.createProgram();
    if (!program)
        throw new Error("Unable to create WebGL program");
    const vertexShader = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER, deferCompletion);
    const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource, deferCompletion);
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.deleteShader(vertexShader);
    gl.deleteShader(fragmentShader);
    if (!deferCompletion && !gl.getProgramParameter(program, gl.LINK_STATUS)) {
        const message = gl.getProgramInfoLog(program) ?? "Unknown program error";
        gl.deleteProgram(program);
        throw new Error(message);
    }
    return program;
}
export function shaderFeaturesForPreset(preset, interaction, optimized) {
    return {
        interaction,
        shape: preset.shape,
        curve: Boolean(preset.curveProfile) &&
            preset.states.some((state) => state.curveStrength !== 0),
        funnel: preset.states.some((state) => (state.funnelStrength ?? 0) !== 0 ||
            (state.funnelContourFloor ?? 0) !== 0),
        optimized,
    };
}
export function shaderFeaturesForState(preset, state, interaction, optimized) {
    if (!optimized)
        return shaderFeaturesForPreset(preset, interaction, false);
    return {
        interaction,
        shape: preset.shape,
        curve: Boolean(preset.curveProfile) && Math.abs(state.curveStrength) > 0.000001,
        funnel: Math.abs(state.funnelStrength) > 0.000001 ||
            Math.abs(state.funnelContourFloor) > 0.000001,
        optimized: true,
    };
}
/**
 * How far (in material units) from the line a fragment can be before it is
 * provably background. Negative selects the shader
 * cross-axis deformation bound for broad states; zero disables skipping.
 */
export function coverageMarginForState(state) {
    // Funnel scale varies by row: use a spatial shader bound instead of one
    // scalar margin for the whole viewport. Curved states ARE bounded: the
    // shader's early-out tests `pos`, which already includes the centerline
    // straightening, so the straight-state envelope applies unchanged.
    if (Math.abs(state.funnelStrength) > 0.000001 ||
        Math.abs(state.funnelContourFloor) > 0.000001)
        return -1;
    const authoredSpread = Math.abs(state.fanSpread) * 1.5 + Math.abs(state.twistSpread) * 1.5;
    if (authoredSpread > 0.12)
        return -1;
    const turbulenceAllowance = Math.max(0, state.turbulence - 0.2) * 0.72;
    // A bent centerline shears the material frame slightly against the straight
    // envelope, so curved states carry extra headroom (measured: without it a
    // few pixels at the bend exceed the 2/255 tolerance).
    const curveAllowance = Math.abs(state.curveStrength) > 0.000001 ? 0.14 : 0;
    return 0.56 + authoredSpread + turbulenceAllowance + curveAllowance;
}
export function shaderVariantKey(features) {
    return [
        features.warp ?? "direct",
        features.sharedFirstOctave ? "shared-octave" : "paired-octave",
        features.warpLinear ? "linear" : "manual",
        features.shape,
        features.curve ? "curve" : "straight",
        features.funnel ? "funnel" : "flat",
        features.interaction ? "pointer" : "idle",
        features.optimized ? "optimized" : "baseline",
        (features.paletteRecurrence ?? features.optimized) ? "recurrence" : "cosine",
    ].join(":");
}
export function shaderConstantsForPreset(preset) {
    const palette = RIBBON_PALETTES[preset.palette];
    return {
        palette: {
            phase: palette.phase,
            sweepStart: palette.sweepStart,
            sweepSpan: palette.sweepSpan,
            mixAmount: palette.mixAmount,
        },
        halftone: preset.halftone,
        interaction: {
            feather: preset.interaction.feather,
            coreExclusion: preset.interaction.coreExclusion,
            dotMagnificationGain: preset.interaction.dotMagnificationGain,
            velocityBiasPx: preset.interaction.velocityBiasPx,
            emissionOccupancy: preset.interaction.emissionOccupancy,
            emissionRadiusScale: preset.interaction.emissionRadiusScale,
            emissionSoftnessScale: preset.interaction.emissionSoftnessScale,
            emissionPeelDistancePx: preset.interaction.emissionPeelDistancePx,
            emissionLifetimeSeconds: preset.interaction.emissionLifetimeSeconds,
            emissionOpacity: preset.interaction.emissionOpacity,
            emissionSeed: preset.interaction.emissionSeed,
        },
    };
}
function shaderConstantsKey(constants) {
    const { palette, halftone, interaction } = constants;
    return [
        ...palette.phase,
        palette.sweepStart,
        palette.sweepSpan,
        palette.mixAmount,
        halftone.granularity,
        ...halftone.radius,
        ...halftone.foldFadeAnisotropy,
        halftone.lineFloor,
        halftone.dotGain,
        halftone.edgeSoftness,
        halftone.bloomGain,
        halftone.seed,
        interaction.feather,
        interaction.coreExclusion,
        interaction.dotMagnificationGain,
        interaction.velocityBiasPx,
        interaction.emissionOccupancy,
        interaction.emissionRadiusScale,
        interaction.emissionSoftnessScale,
        ...interaction.emissionPeelDistancePx,
        ...interaction.emissionLifetimeSeconds,
        interaction.emissionOpacity,
        interaction.emissionSeed,
    ].join(",");
}
export function shaderProgramKey(features, constants) {
    return `${shaderVariantKey(features)}|${shaderConstantsKey(constants)}`;
}
export const UNIFORM_NAMES = {
    warpLayer: "uWarpLayer",
    warpSize: "uWarpSize",
    warpField: "uWarpField",
    resolution: "uResolution",
    cssResolution: "uCssResolution",
    crossAxisScale: "uCrossAxisScale",
    sceneHeightRatio: "uSceneHeightRatio",
    framingAspect: "uFramingAspect",
    time: "uTime",
    bgColor: "uBgColor",
    zoom: "uZoom",
    center: "uCenter",
    lineExtent: "uLineExtent",
    rotation: "uRotation",
    palettePhase: "uPalettePhase",
    paletteStart: "uPaletteStart",
    paletteSpan: "uPaletteSpan",
    spatialColor: "uSpatialColor",
    mixAmount: "uMixAmount",
    spacing: "uSpacing",
    dither: "uDither",
    amplitude: "uAmplitude",
    speed: "uSpeed",
    twistRetention: "uTwistRetention",
    fanSpread: "uFanSpread",
    twistSpread: "uTwistSpread",
    horizontalStretch: "uHorizontalStretch",
    unravel: "uUnravel",
    funnelStrength: "uFunnelStrength",
    funnelWidth: "uFunnelWidth",
    funnelTaperCeiling: "uFunnelTaperCeiling",
    funnelContourFloor: "uFunnelContourFloor",
    funnelTurbulence: "uFunnelTurbulence",
    funnelSpacing: "uFunnelSpacing",
    funnelLineDefinition: "uFunnelLineDefinition",
    curveStrength: "uCurveStrength",
    curveDisplacementScale: "uCurveDisplacementScale",
    curveProgress: "uCurveProgress",
    curveViewportSpan: "uCurveViewportSpan",
    curveXCheckpoints: "uCurveXCheckpoints",
    curveBendWindows: "uCurveBendWindows",
    curveStartWidthScale: "uCurveStartWidthScale",
    effectOpacity: "uEffectOpacity",
    edgeReveal: "uEdgeReveal",
    coverageMargin: "uCoverageMargin",
    shapeType: "uShapeType",
    pointerPx: "uPointerPx",
    pointerVelocity: "uPointerVelocity",
    pointerStrength: "uPointerStrength",
    pointerStrengthScale: "uPointerStrengthScale",
    pointerFunnelStrengthScale: "uPointerFunnelStrengthScale",
    pointerRadiusPx: "uPointerRadiusPx",
    pointerFeather: "uPointerFeather",
    pointerCoreExclusion: "uPointerCoreExclusion",
    pointerMaxMagnification: "uPointerMaxMagnification",
    pointerDotMagnificationGain: "uPointerDotMagnificationGain",
    pointerSpreadPx: "uPointerSpreadPx",
    pointerVelocityBiasPx: "uPointerVelocityBiasPx",
    halftoneGranularity: "uHalftoneGranularity",
    halftoneApparentZoom: "uHalftoneApparentZoom",
    halftoneRadius: "uHalftoneRadius",
    halftoneFoldFadeAnisotropy: "uHalftoneFoldFadeAnisotropy",
    halftoneLineFloor: "uHalftoneLineFloor",
    halftoneDotGain: "uHalftoneDotGain",
    halftoneEdgeSoftness: "uHalftoneEdgeSoftness",
    halftoneBloomGain: "uHalftoneBloomGain",
    halftoneSeed: "uHalftoneSeed",
    emissionOccupancy: "uEmissionOccupancy",
    emissionRadiusScale: "uEmissionRadiusScale",
    emissionSoftnessScale: "uEmissionSoftnessScale",
    emissionPeelPx: "uEmissionPeelPx",
    emissionLifetime: "uEmissionLifetime",
    emissionOpacity: "uEmissionOpacity",
    emissionSeed: "uEmissionSeed",
};
export function getLocations(gl, program) {
    const entries = Object.entries(UNIFORM_NAMES).map(([key, name]) => [
        key,
        gl.getUniformLocation(program, name),
    ]);
    return Object.fromEntries(entries);
}
/**
 * One cache per WebGL context. `compile` is synchronous and always returns a
 * usable bundle (falling back to the broad compatibility program); `prewarm`
 * compiles in the background when the driver supports parallel compilation.
 */
export class ProgramCache {
    gl;
    programs = new Map();
    pending = new Map();
    fallbackBundle = null;
    parallel;
    disposed = false;
    constructor(gl) {
        this.gl = gl;
        this.parallel = gl.getExtension("KHR_parallel_shader_compile");
    }
    get parallelCompile() {
        return this.parallel !== null;
    }
    get(features, constants) {
        return this.programs.get(shaderProgramKey(features, constants)) ?? null;
    }
    createBundle(program, features, cacheKey) {
        const { gl } = this;
        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            const message = gl.getProgramInfoLog(program) ?? "Unknown program error";
            gl.deleteProgram(program);
            throw new Error(message);
        }
        return {
            program,
            loc: getLocations(gl, program),
            position: gl.getAttribLocation(program, "aPosition"),
            variant: shaderVariantKey(features),
            interaction: features.interaction,
            cacheKey,
        };
    }
    compile(features, constants) {
        const { gl } = this;
        const cacheKey = shaderProgramKey(features, constants);
        const cached = this.programs.get(cacheKey);
        if (cached)
            return cached;
        try {
            const pending = this.pending.get(cacheKey);
            if (pending) {
                window.clearTimeout(pending.timer);
                this.pending.delete(cacheKey);
                const bundle = this.createBundle(pending.program, features, cacheKey);
                this.programs.set(cacheKey, bundle);
                pending.callbacks.forEach((callback) => callback(bundle));
                return bundle;
            }
            const program = createProgram(gl, createFragmentShader(features, constants));
            const bundle = this.createBundle(program, features, cacheKey);
            this.programs.set(cacheKey, bundle);
            return bundle;
        }
        catch (error) {
            console.warn(`Ribbon shader variant ${shaderVariantKey(features)} failed; using compatibility shader`, error);
            if (!this.fallbackBundle) {
                const program = createProgram(gl);
                this.fallbackBundle = {
                    program,
                    loc: getLocations(gl, program),
                    position: gl.getAttribLocation(program, "aPosition"),
                    variant: "compatibility",
                    interaction: true,
                    cacheKey: "compatibility",
                };
            }
            return this.fallbackBundle;
        }
    }
    /** Returns false when parallel compilation is unavailable (caller compiles later). */
    prewarm(features, constants, callback = () => { }) {
        if (!this.parallel)
            return false;
        const { gl } = this;
        const parallel = this.parallel;
        const cacheKey = shaderProgramKey(features, constants);
        const cached = this.programs.get(cacheKey);
        if (cached) {
            callback(cached);
            return true;
        }
        const existing = this.pending.get(cacheKey);
        if (existing) {
            existing.callbacks.push(callback);
            return true;
        }
        const program = createProgram(gl, createFragmentShader(features, constants), true);
        const pending = { program, timer: 0, callbacks: [callback] };
        const poll = () => {
            if (this.disposed)
                return;
            const complete = gl.getProgramParameter(program, parallel.COMPLETION_STATUS_KHR);
            if (!complete) {
                pending.timer = window.setTimeout(poll, 16);
                return;
            }
            this.pending.delete(cacheKey);
            try {
                const bundle = this.createBundle(program, features, cacheKey);
                this.programs.set(cacheKey, bundle);
                pending.callbacks.forEach((ready) => ready(bundle));
            }
            catch (error) {
                console.warn(`Ribbon shader variant ${shaderVariantKey(features)} failed to precompile`, error);
                pending.callbacks.forEach((ready) => ready(null));
            }
        };
        this.pending.set(cacheKey, pending);
        pending.timer = window.setTimeout(poll, 0);
        return true;
    }
    destroy() {
        this.disposed = true;
        const { gl } = this;
        this.pending.forEach((pending) => {
            window.clearTimeout(pending.timer);
            gl.deleteProgram(pending.program);
        });
        this.pending.clear();
        const deleted = new Set();
        this.programs.forEach((bundle) => {
            if (!deleted.has(bundle.program)) {
                deleted.add(bundle.program);
                gl.deleteProgram(bundle.program);
            }
        });
        this.programs.clear();
        if (this.fallbackBundle && !deleted.has(this.fallbackBundle.program)) {
            gl.deleteProgram(this.fallbackBundle.program);
        }
        this.fallbackBundle = null;
    }
}
//# sourceMappingURL=program-cache.js.map